import { useState } from 'react';
import clsx from 'clsx';
import { plusBlack } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import BottomDrawer from '../../../../../../common/_custom/BottomDrawer/BottomDrawer';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import { ensureHttpsPrefix } from '../../../../../../utils/utils';
import { KEYBOARD_EVENTS } from '../../../../../../utils/constants/commonConstants';
import { ADD_LINKEDIN_KEY, getSocialsDisplay, MAX_VISIBLE_SOCIALS, Social } from './socialPlatforms';
import styles from './SocialsRow.module.scss';

// Delay (ms) before the "+n more" overflow popover hides, so the cursor can travel into it.
const OVERFLOW_HIDE_DELAY_MS = 500;

// If a brand icon fails to load, hide the broken-image glyph rather than show it.
const hideBrokenIcon = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.visibility = 'hidden';
};

interface SocialsRowProps {
    socials?: Social[];
    isValidUser: boolean;
    onAddLinkedin: () => void;
    // Edit-profile LinkedIn URL — takes priority over the LLM-provided one.
    userInputLinkedin?: string;
}

const SocialsRow = ({ socials, isValidUser, onAddLinkedin, userInputLinkedin }: SocialsRowProps) => {
    const { chips, overflow } = getSocialsDisplay(socials, { isValidUser, userInputLinkedin });
    // Hover popovers don't work on touch, so mobile uses a tap-opened bottom sheet instead.
    const isMobile = useIsMobile();
    const [sheetOpen, setSheetOpen] = useState(false);

    // Nothing to show (empty or absent socials, and no add chip) — render no row.
    if (!chips.length) return null;

    const hasAddChip = chips[0].key === ADD_LINKEDIN_KEY;

    // Real socials (everything except the Add-LinkedIn chip): visible chips + overflow.
    const allSocials = [...chips.filter((chip) => chip.key !== ADD_LINKEDIN_KEY), ...overflow];

    // ---- Mobile: a tappable preview stack that opens a bottom sheet of socials ----
    if (isMobile) {
        // No real socials → hide the row entirely (incl. the "+" add chip and its space).
        if (!allSocials.length) return null;

        const preview = allSocials.slice(0, MAX_VISIBLE_SOCIALS);
        const remaining = allSocials.length - preview.length;

        return (
            <>
                <div
                    className={styles.SocialsRow}
                    role="button"
                    tabIndex={0}
                    aria-label="View socials"
                    onClick={() => setSheetOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) setSheetOpen(true);
                    }}
                >
                    {preview.map((chip) => (
                        <span key={chip.key} className={styles.Chip}>
                            <img src={chip.icon} alt="" onError={hideBrokenIcon} />
                        </span>
                    ))}
                    {remaining > 0 && <span className={styles.MoreLabel}>+{remaining} more</span>}
                </div>
                {sheetOpen && (
                    <BottomDrawer onClose={() => setSheetOpen(false)} height="auto" showHeading heading="Socials">
                        <div className={styles.SheetList}>
                            {allSocials.map((chip) => (
                                <a
                                    key={chip.key}
                                    className={styles.SheetRow}
                                    href={ensureHttpsPrefix(chip.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img src={chip.icon} alt="" onError={hideBrokenIcon} />
                                    <span>{chip.label}</span>
                                </a>
                            ))}
                        </div>
                    </BottomDrawer>
                )}
            </>
        );
    }

    // ---- Desktop: hover chips + "+n more" popover (unchanged) ----
    return (
        <div className={clsx(styles.SocialsRow, hasAddChip && styles.hasAdd)}>
            {chips.map((chip) =>
                chip.key === ADD_LINKEDIN_KEY ? (
                    <Tooltip
                        key={chip.key}
                        content="Add LinkedIn"
                        direction="bottom"
                        small
                        fitContent
                        contentCss={{ whiteSpace: 'nowrap' }}
                    >
                        <div
                            className={clsx(styles.Chip, styles.ChipAdd)}
                            onClick={onAddLinkedin}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) onAddLinkedin();
                            }}
                        >
                            <img src={plusBlack} alt="Add" />
                        </div>
                    </Tooltip>
                ) : (
                    <Tooltip key={chip.key} content={chip.label} direction="bottom" small fitContent contentCss={{ whiteSpace: 'nowrap' }}>
                        <a className={styles.Chip} href={ensureHttpsPrefix(chip.url)} target="_blank" rel="noopener noreferrer">
                            <img src={chip.icon} alt={chip.name} onError={hideBrokenIcon} />
                        </a>
                    </Tooltip>
                ),
            )}
            {overflow.length > 0 && (
                <Tooltip
                    content={
                        <div className={styles.OverflowList}>
                            {overflow.map((chip) => (
                                <a
                                    key={chip.key}
                                    className={styles.OverflowRow}
                                    href={ensureHttpsPrefix(chip.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img src={chip.icon} alt="" onError={hideBrokenIcon} />
                                    <span>{chip.label}</span>
                                </a>
                            ))}
                        </div>
                    }
                    direction="bottom"
                    small
                    fitContent
                    hideDelay={OVERFLOW_HIDE_DELAY_MS}
                >
                    <span className={styles.MoreLabel}>+{overflow.length} more</span>
                </Tooltip>
            )}
        </div>
    );
};

export default SocialsRow;
